"use strict";
/**
 * Copyright 2018 Novage LLC.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HybridLoader = void 0;
const debug_1 = __importDefault(require("debug"));
const events_1 = require("events");
const simple_peer_1 = __importDefault(require("simple-peer"));
const loader_interface_1 = require("./loader-interface");
const http_media_manager_1 = require("./http-media-manager");
const p2p_media_manager_1 = require("./p2p-media-manager");
const media_peer_1 = require("./media-peer");
const bandwidth_approximator_1 = require("./bandwidth-approximator");
const segments_memory_storage_1 = require("./segments-memory-storage");
const defaultSettings = {
    cachedSegmentExpiration: 5 * 60 * 1000,
    cachedSegmentsCount: 30,
    useP2P: true,
    consumeOnly: false,
    requiredSegmentsPriority: 1,
    skipSegmentBuilderPriority: 1,
    simultaneousHttpDownloads: 2,
    httpDownloadProbability: 0.1,
    httpDownloadProbabilityInterval: 1000,
    httpDownloadProbabilitySkipIfNoPeers: false,
    httpFailedSegmentTimeout: 10000,
    httpDownloadMaxPriority: 20,
    httpDownloadInitialTimeout: 0,
    httpDownloadInitialTimeoutPerSegment: 4000,
    httpUseRanges: false,
    simultaneousP2PDownloads: 3,
    p2pDownloadMaxPriority: 20,
    p2pSegmentDownloadTimeout: 60000,
    webRtcMaxMessageSize: 64 * 1024 - 1,
    trackerAnnounce: ["wss://tracker.novage.com.ua", "wss://tracker.openwebtorrent.com"],
    peerRequestsPerAnnounce: 10,
    rtcConfig: simple_peer_1.default.config,
};
class HybridLoader extends events_1.EventEmitter {
    constructor(settings = {}) {
        super();
        this.debug = (0, debug_1.default)("p2pml:hybrid-loader");
        this.debugSegments = (0, debug_1.default)("p2pml:hybrid-loader-segments");
        this.segmentsQueue = [];
        this.bandwidthApproximator = new bandwidth_approximator_1.BandwidthApproximator();
        this.httpDownloadInitialTimeoutTimestamp = -Infinity;
        this.createHttpManager = () => {
            return new http_media_manager_1.HttpMediaManager(this.settings);
        };
        this.createP2PManager = () => {
            return new p2p_media_manager_1.P2PMediaManager(this.segmentsStorage, this.settings);
        };
        this.load = async (segments, streamSwarmId) => {
            this.initRandomDownloadIntervalIfNeeded();
            if (segments.length > 0) {
                this.masterSwarmId = segments[0].masterSwarmId;
            }
            if (this.masterSwarmId !== undefined) {
                this.p2pManager.setStreamSwarmId(streamSwarmId, this.masterSwarmId);
            }
            this.debug("load segments");
            // stop all http requests and p2p downloads for segments that are not in the new load
            let updateSegmentsMap = this.abortUnknownSegments(segments);
            if (this.debug.enabled) {
                for (const segment of segments) {
                    if (!this.segmentsQueue.find((f) => f.id === segment.id)) {
                        this.debug("add segment", segment.id);
                    }
                }
            }
            this.segmentsQueue = segments;
            if (this.masterSwarmId === undefined) {
                return;
            }
            let storageSegments = await this.segmentsStorage.getSegmentsMap(this.masterSwarmId);
            updateSegmentsMap = this.processSegmentsQueue(storageSegments) || updateSegmentsMap;
            if (await this.cleanSegmentsStorage()) {
                storageSegments = await this.segmentsStorage.getSegmentsMap(this.masterSwarmId);
                updateSegmentsMap = true;
            }
            if (updateSegmentsMap && !this.settings.consumeOnly) {
                this.p2pManager.sendSegmentsMapToAll(this.createSegmentsMap(storageSegments));
            }
        };
        this.getSegment = async (id) => {
            return this.masterSwarmId === undefined ? undefined : this.segmentsStorage.getSegment(id, this.masterSwarmId);
        };
        this.getSettings = () => {
            return this.settings;
        };
        this.getDetails = () => {
            return {
                peerId: this.p2pManager.getPeerId(),
            };
        };
        this.getBandwidthEstimate = () => {
            return this.bandwidthApproximator.getBandwidth(this.now());
        };
        this.destroy = async () => {
            if (this.httpRandomDownloadInterval !== undefined) {
                clearInterval(this.httpRandomDownloadInterval);
                this.httpRandomDownloadInterval = undefined;
            }
            this.httpDownloadInitialTimeoutTimestamp = -Infinity;
            this.segmentsQueue = [];
            this.httpManager.destroy();
            this.p2pManager.destroy();
            this.masterSwarmId = undefined;
            await this.segmentsStorage.destroy();
        };
        this.processInitialSegmentTimeout = async () => {
            if (this.httpRandomDownloadInterval === undefined) {
                return; // Instance destroyed
            }
            if (this.masterSwarmId !== undefined) {
                const storageSegments = await this.segmentsStorage.getSegmentsMap(this.masterSwarmId);
                if (this.processSegmentsQueue(storageSegments) && !this.settings.consumeOnly) {
                    this.p2pManager.sendSegmentsMapToAll(this.createSegmentsMap(storageSegments));
                }
            }
            if (this.httpDownloadInitialTimeoutTimestamp !== -Infinity) {
                // Set one more timeout for a next segment
                setTimeout(this.processInitialSegmentTimeout, this.settings.httpDownloadInitialTimeoutPerSegment);
            }
        };
        this.processSegmentsQueue = (storageSegments) => {
            const currentPriority = this.segmentsQueue.length > 0 ? this.segmentsQueue[0].priority : 0;
            this.debugSegments(`process segments queue. priority: ${currentPriority}, queue length: ${this.segmentsQueue.length}`);
            if (this.masterSwarmId === undefined || this.segmentsQueue.length === 0) {
                return false;
            }
            let updateSegmentsMap = false;
            let segmentsMap;
            let httpAllowed = true;
            if (this.httpDownloadInitialTimeoutTimestamp !== -Infinity) {
                let firstNotDownloadePriority;
                for (const segment of this.segmentsQueue) {
                    if (!storageSegments.has(segment.id)) {
                        firstNotDownloadePriority = segment.priority;
                        break;
                    }
                }
                const httpTimeout = this.now() - this.httpDownloadInitialTimeoutTimestamp;
                httpAllowed =
                    httpTimeout >= this.settings.httpDownloadInitialTimeout ||
                        (firstNotDownloadePriority !== undefined &&
                            httpTimeout > this.settings.httpDownloadInitialTimeoutPerSegment &&
                            firstNotDownloadePriority <= 0);
                if (httpAllowed) {
                    this.debugSegments("cancel initial HTTP download timeout - timed out");
                    this.httpDownloadInitialTimeoutTimestamp = -Infinity;
                }
            }
            let scheduleNewProcessQueue = false;
            for (let index = 0; index < this.segmentsQueue.length; index++) {
                const segment = this.segmentsQueue[index];
                if (storageSegments.has(segment.id)) {
                    continue;
                }
                // Segment priority changed, notify http manager
                if (this.httpManager.isDownloading(segment)) {
                    this.httpManager.updatePriority(segment);
                    continue;
                }
                const tryHTTP = httpAllowed && segment.priority <= this.settings.requiredSegmentsPriority;
                if (tryHTTP && !this.httpManager.isFailed(segment)) {
                    // Download required segments over HTTP
                    if (this.httpManager.getActiveDownloadsCount() >= this.settings.simultaneousHttpDownloads) {
                        // Not enough HTTP download resources. Abort one of the HTTP downloads.
                        for (let i = this.segmentsQueue.length - 1; i > index; i--) {
                            const segmentToAbort = this.segmentsQueue[i];
                            if (this.httpManager.isDownloading(segmentToAbort)) {
                                this.debugSegments("cancel HTTP download", segmentToAbort.priority, segmentToAbort.id);
                                this.httpManager.abort(segmentToAbort);
                                break;
                            }
                        }
                    }
                    if (this.httpManager.getActiveDownloadsCount() < this.settings.simultaneousHttpDownloads) {
                        // Abort P2P download of the required segment if any and force HTTP download
                        const downloadedPieces = this.p2pManager.abort(segment);
                        this.httpManager.download(segment, downloadedPieces);
                        this.debugSegments("HTTP download (priority)", segment.priority, segment.id);
                        updateSegmentsMap = true;
                        continue;
                    }
                }
                // We wanted to download a failed segment through HTTP, but we could not because of the timeout.
                // Then we need to schedule another processing queue task
                if (tryHTTP && this.httpManager.isFailed(segment)) {
                    scheduleNewProcessQueue = true;
                }
                if (this.p2pManager.isDownloading(segment)) {
                    continue;
                }
                if (segment.priority <= this.settings.requiredSegmentsPriority) {
                    // Download required segments over P2P
                    segmentsMap = segmentsMap ? segmentsMap : this.p2pManager.getOverallSegmentsMap();
                    if (segmentsMap.get(segment.id) !== media_peer_1.MediaPeerSegmentStatus.Loaded) {
                        continue;
                    }
                    if (this.p2pManager.getActiveDownloadsCount() >= this.settings.simultaneousP2PDownloads) {
                        // Not enough P2P download resources. Abort one of the P2P downloads.
                        for (let i = this.segmentsQueue.length - 1; i > index; i--) {
                            const segmentToAbort = this.segmentsQueue[i];
                            if (this.p2pManager.isDownloading(segmentToAbort)) {
                                this.debugSegments("cancel P2P download", segmentToAbort.priority, segmentToAbort.id);
                                this.p2pManager.abort(segmentToAbort);
                                break;
                            }
                        }
                    }
                    if (this.p2pManager.getActiveDownloadsCount() < this.settings.simultaneousP2PDownloads) {
                        if (this.p2pManager.download(segment)) {
                            this.debugSegments("P2P download (priority)", segment.priority, segment.id);
                            continue;
                        }
                    }
                    continue;
                }
                if (this.p2pManager.getActiveDownloadsCount() < this.settings.simultaneousP2PDownloads &&
                    segment.priority <= this.settings.p2pDownloadMaxPriority) {
                    if (this.p2pManager.download(segment)) {
                        this.debugSegments("P2P download", segment.priority, segment.id);
                    }
                }
            }
            if (scheduleNewProcessQueue) {
                setTimeout(async () => {
                    if (this.masterSwarmId === undefined)
                        return;
                    const storageSegments = await this.segmentsStorage.getSegmentsMap(this.masterSwarmId);
                    this.processSegmentsQueue(storageSegments);
                }, this.settings.httpFailedSegmentTimeout);
            }
            return updateSegmentsMap;
        };
        this.downloadRandomSegmentOverHttp = async () => {
            if (this.masterSwarmId === undefined ||
                this.httpRandomDownloadInterval === undefined ||
                this.httpDownloadInitialTimeoutTimestamp !== -Infinity ||
                this.httpManager.getActiveDownloadsCount() >= this.settings.simultaneousHttpDownloads ||
                (this.settings.httpDownloadProbabilitySkipIfNoPeers && this.p2pManager.getPeers().size === 0) ||
                this.settings.consumeOnly) {
                return;
            }
            const storageSegments = await this.segmentsStorage.getSegmentsMap(this.masterSwarmId);
            const segmentsMap = this.p2pManager.getOverallSegmentsMap();
            const pendingQueue = this.segmentsQueue.filter((s) => !this.p2pManager.isDownloading(s) &&
                !this.httpManager.isDownloading(s) &&
                !segmentsMap.has(s.id) &&
                !this.httpManager.isFailed(s) &&
                s.priority <= this.settings.httpDownloadMaxPriority &&
                !storageSegments.has(s.id));
            if (pendingQueue.length === 0) {
                return;
            }
            if (Math.random() > this.settings.httpDownloadProbability * pendingQueue.length) {
                return;
            }
            const segment = pendingQueue[Math.floor(Math.random() * pendingQueue.length)];
            this.debugSegments("HTTP download (random)", segment.priority, segment.id);
            this.httpManager.download(segment);
            this.p2pManager.sendSegmentsMapToAll(this.createSegmentsMap(storageSegments));
        };
        this.onSegmentStartLoad = (method, segment) => {
            this.emit(loader_interface_1.Events.SegmentStartLoad, method, segment);
        };
        this.onPieceBytesDownloaded = (method, segment, bytes, peerId) => {
            this.bandwidthApproximator.addBytes(bytes, this.now());
            this.emit(loader_interface_1.Events.PieceBytesDownloaded, method, segment, bytes, peerId);
        };
        this.onPieceBytesUploaded = (method, segment, bytes, peerId) => {
            this.emit(loader_interface_1.Events.PieceBytesUploaded, method, segment, bytes, peerId);
        };
        this.onSegmentLoaded = async (segment, data, peerId) => {
            this.debugSegments("segment loaded", segment.id, segment.id);
            if (this.masterSwarmId === undefined) {
                return;
            }
            segment.data = data;
            segment.downloadBandwidth = this.bandwidthApproximator.getBandwidth(this.now());
            await this.segmentsStorage.storeSegment(segment);
            this.emit(loader_interface_1.Events.SegmentLoaded, segment, peerId);
            const storageSegments = await this.segmentsStorage.getSegmentsMap(this.masterSwarmId);
            this.processSegmentsQueue(storageSegments);
            if (!this.settings.consumeOnly) {
                this.p2pManager.sendSegmentsMapToAll(this.createSegmentsMap(storageSegments));
            }
        };
        this.onSegmentError = async (segment, details, peerId) => {
            this.debugSegments("segment error", segment.id, segment.id, peerId, details);
            this.emit(loader_interface_1.Events.SegmentError, segment, details, peerId);
            if (this.masterSwarmId !== undefined) {
                const storageSegments = await this.segmentsStorage.getSegmentsMap(this.masterSwarmId);
                if (this.processSegmentsQueue(storageSegments) && !this.settings.consumeOnly) {
                    this.p2pManager.sendSegmentsMapToAll(this.createSegmentsMap(storageSegments));
                }
            }
        };
        this.onSegmentSize = async (segment, size) => {
            this.debugSegments("segment size", segment.id, size);
            this.emit(loader_interface_1.Events.SegmentSize, segment, size);
        };
        this.getStreamSwarmId = (segment) => {
            return segment.streamId === undefined ? segment.masterSwarmId : `${segment.masterSwarmId}+${segment.streamId}`;
        };
        this.createSegmentsMap = (storageSegments) => {
            const segmentsMap = {};
            const addSegmentToMap = (segment, status) => {
                const streamSwarmId = this.getStreamSwarmId(segment);
                const segmentId = segment.sequence;
                let segmentsIdsAndStatuses = segmentsMap[streamSwarmId];
                if (segmentsIdsAndStatuses === undefined) {
                    segmentsIdsAndStatuses = ["", []];
                    segmentsMap[streamSwarmId] = segmentsIdsAndStatuses;
                }
                const segmentsStatuses = segmentsIdsAndStatuses[1];
                segmentsIdsAndStatuses[0] += segmentsStatuses.length === 0 ? segmentId : `|${segmentId}`;
                segmentsStatuses.push(status);
            };
            for (const storageSegment of storageSegments.values()) {
                addSegmentToMap(storageSegment.segment, media_peer_1.MediaPeerSegmentStatus.Loaded);
            }
            for (const download of this.httpManager.getActiveDownloads().values()) {
                addSegmentToMap(download.segment, media_peer_1.MediaPeerSegmentStatus.LoadingByHttp);
            }
            return segmentsMap;
        };
        this.onPeerConnect = async (peer) => {
            this.emit(loader_interface_1.Events.PeerConnect, peer);
            if (!this.settings.consumeOnly && this.masterSwarmId !== undefined) {
                this.p2pManager.sendSegmentsMap(peer.id, this.createSegmentsMap(await this.segmentsStorage.getSegmentsMap(this.masterSwarmId)));
            }
        };
        this.onPeerClose = (peerId) => {
            this.emit(loader_interface_1.Events.PeerClose, peerId);
        };
        this.onTrackerUpdate = async (data) => {
            if (this.httpDownloadInitialTimeoutTimestamp !== -Infinity &&
                data.incomplete !== undefined &&
                data.incomplete <= 1) {
                this.debugSegments("cancel initial HTTP download timeout - no peers");
                this.httpDownloadInitialTimeoutTimestamp = -Infinity;
                if (this.masterSwarmId !== undefined) {
                    const storageSegments = await this.segmentsStorage.getSegmentsMap(this.masterSwarmId);
                    if (this.processSegmentsQueue(storageSegments) && !this.settings.consumeOnly) {
                        this.p2pManager.sendSegmentsMapToAll(this.createSegmentsMap(storageSegments));
                    }
                }
            }
        };
        this.cleanSegmentsStorage = async () => {
            if (this.masterSwarmId === undefined) {
                return false;
            }
            return this.segmentsStorage.clean(this.masterSwarmId, (id) => this.segmentsQueue.find((queueSegment) => queueSegment.id === id) !== undefined);
        };
        this.now = () => {
            return performance.now();
        };
        this.initRandomDownloadIntervalIfNeeded = () => {
            if (this.httpRandomDownloadInterval !== undefined)
                return;
            // Do once on first call
            this.httpRandomDownloadInterval = setInterval(this.downloadRandomSegmentOverHttp, this.settings.httpDownloadProbabilityInterval);
            if (this.settings.httpDownloadInitialTimeout > 0 &&
                this.settings.httpDownloadInitialTimeoutPerSegment > 0) {
                // Initialize initial HTTP download timeout (i.e. download initial segments over P2P)
                this.debugSegments("enable initial HTTP download timeout", this.settings.httpDownloadInitialTimeout, "per segment", this.settings.httpDownloadInitialTimeoutPerSegment);
                this.httpDownloadInitialTimeoutTimestamp = this.now();
                setTimeout(this.processInitialSegmentTimeout, this.settings.httpDownloadInitialTimeoutPerSegment + 100);
            }
        };
        this.abortUnknownSegments = (segments) => {
            let updateSegmentsMap = false;
            for (const segment of this.segmentsQueue) {
                if (!segments.find((f) => f.id === segment.id)) {
                    this.debug("remove segment", segment.id);
                    if (this.httpManager.isDownloading(segment)) {
                        updateSegmentsMap = true;
                        this.httpManager.abort(segment);
                    }
                    else {
                        this.p2pManager.abort(segment);
                    }
                    this.emit(loader_interface_1.Events.SegmentAbort, segment);
                }
            }
            return updateSegmentsMap;
        };
        this.settings = Object.assign(Object.assign({}, defaultSettings), settings);
        const { bufferedSegmentsCount } = settings;
        if (typeof bufferedSegmentsCount === "number") {
            if (settings.p2pDownloadMaxPriority === undefined) {
                this.settings.p2pDownloadMaxPriority = bufferedSegmentsCount;
            }
            if (settings.httpDownloadMaxPriority === undefined) {
                this.settings.p2pDownloadMaxPriority = bufferedSegmentsCount;
            }
        }
        this.segmentsStorage =
            this.settings.segmentsStorage === undefined
                ? new segments_memory_storage_1.SegmentsMemoryStorage(this.settings)
                : this.settings.segmentsStorage;
        this.debug("loader settings", this.settings);
        this.httpManager = this.createHttpManager();
        this.httpManager.on("segment-start-load", (segment) => this.onSegmentStartLoad("http", segment));
        this.httpManager.on("segment-loaded", this.onSegmentLoaded);
        this.httpManager.on("segment-error", this.onSegmentError);
        this.httpManager.on("segment-size", this.onSegmentSize);
        this.httpManager.on("bytes-downloaded", (segment, bytes) => {
            this.onPieceBytesDownloaded("http", segment, bytes);
        });
        this.p2pManager = this.createP2PManager();
        this.p2pManager.on("segment-start-load", (segment) => this.onSegmentStartLoad("p2p", segment));
        this.p2pManager.on("segment-loaded", this.onSegmentLoaded);
        this.p2pManager.on("segment-error", this.onSegmentError);
        this.p2pManager.on("segment-size", this.onSegmentSize);
        this.p2pManager.on("peer-data-updated", async () => {
            if (this.masterSwarmId === undefined) {
                return;
            }
            const storageSegments = await this.segmentsStorage.getSegmentsMap(this.masterSwarmId);
            if (this.processSegmentsQueue(storageSegments) && !this.settings.consumeOnly) {
                this.p2pManager.sendSegmentsMapToAll(this.createSegmentsMap(storageSegments));
            }
        });
        this.p2pManager.on("bytes-downloaded", (segment, bytes, peerId) => this.onPieceBytesDownloaded("p2p", segment, bytes, peerId));
        this.p2pManager.on("bytes-uploaded", (segment, bytes, peerId) => this.onPieceBytesUploaded("p2p", segment, bytes, peerId));
        this.p2pManager.on("peer-connected", this.onPeerConnect);
        this.p2pManager.on("peer-closed", this.onPeerClose);
        this.p2pManager.on("tracker-update", this.onTrackerUpdate);
    }
}
exports.HybridLoader = HybridLoader;
HybridLoader.isSupported = () => {
    return window.RTCPeerConnection.prototype.createDataChannel !== undefined;
};
